import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import {appHistory} from '../../history';

import {ErrorState} from './ErrorState';
import {Header} from './header';
import {PreferenceCenter} from './preference-center';
import {PreferenceCenterResubscribe} from './preference-center-resubscribe';
import {ResubscribedPage} from './resubscribed-page';
import type {Audience} from './types';
import {UnsubscribedPage} from './unsubscribed-page';
import {UpdatePreferences} from './update-preferences';

import styles from './unsubscribe-root.module.css';

// We should do this with routing, but that doesnt work with keeping the URL
// state and if we get rid of the URL values, we lose all data about the user
const PREFERENCES_VIEW = 'PREFERENCES_VIEW';
const UNSUBSCRIBED_VIEW = 'UNSUBSCRIBED_VIEW';
const RESUBSCRIBED_VIEW = 'RESUBSCRIBED_VIEW';

type View = 'PREFERENCES_VIEW' | 'UNSUBSCRIBED_VIEW' | 'RESUBSCRIBED_VIEW';

type Props = {
    // These props actually are used, but with passing all props into the render
    // function, then passing all props into another component that uses these
    // props breaks flow
    emailAddress?: string;
    showSuccessBanner?: boolean;
    isGloballyUnsubscribed?: boolean;

    campaignName?: string;

    unsubscribeUrl?: string;

    preferencesUrl?: string;

    allowAudienceUnsubscribe?: boolean;
    companyName?: string;
    isGeneric?: string;
    publicAudiences: Audience[];

    currentAudience?: Audience;
};

export function UnsubscribeRoot(props: Props) {
    const [view, setView] = React.useState<View>(
        props.isGloballyUnsubscribed ? UNSUBSCRIBED_VIEW : PREFERENCES_VIEW
    );

    return (
        <div className={styles.background}>
            <div className={styles.container}>
                <StackLegacy spacing={16}>
                    {props.showSuccessBanner && (
                        <>
                            {appHistory.replace({search: ''})}
                            <FlashBanner duration={3000} type='success-dark'>
                                Your preferences have been updated
                            </FlashBanner>
                        </>
                    )}
                    <Header emailAddress={props.emailAddress} isGeneric={props.isGeneric} />
                    {props.publicAudiences.length > 0
                        ? renderPreferenceCenter(props)
                        : renderPageContents(props, view, setView)}
                </StackLegacy>
            </div>
            {props.companyName ? (
                <div className={styles.footer}>Sent from {props.companyName}</div>
            ) : undefined}
        </div>
    );
}

function renderPreferenceCenter(props: Props) {
    const currentAudience =
        (props.allowAudienceUnsubscribe || props.isGloballyUnsubscribed) &&
        props.currentAudience &&
        props.campaignName
            ? {
                  id: props.currentAudience.id,
                  name: 'Content like this',
                  description: `Messages in the ${props.campaignName} newsletter`,
                  isSubscribed: props.currentAudience.isSubscribed,
              }
            : undefined;

    const allAudiences = currentAudience
        ? [currentAudience, ...props.publicAudiences]
        : props.publicAudiences;

    if (props.isGloballyUnsubscribed) {
        return (
            <PreferenceCenterResubscribe
                publicAudiences={allAudiences}
                resubscribeUrl={props.preferencesUrl}
                onSuccessfulResubscribe={() => {
                    appHistory.push('?showSuccessBanner=true');
                    window.location.reload();
                }}
            />
        );
    } else {
        return (
            <PreferenceCenter
                publicAudiences={allAudiences}
                unsubscribeUrl={props.unsubscribeUrl}
                preferencesUrl={props.preferencesUrl}
                onSuccessfulUnsubscribe={() => {
                    appHistory.push('?showSuccessBanner=true');
                    window.location.reload();
                }}
            />
        );
    }
}

function renderPageContents(props: Props, currentView: View, setView: (arg1: View) => void) {
    const url = props.unsubscribeUrl;

    if (props.isGeneric) {
        return (
            <UpdatePreferences
                // @ts-expect-error
                emailAddress='[your recipient’s email address]'
                campaignName={props.campaignName}
                allowAudienceUnsubscribe={props.allowAudienceUnsubscribe}
                isGeneric={true}
            />
        );
    }

    if (!url) {
        return <ErrorState />;
    }

    if (currentView === PREFERENCES_VIEW) {
        return (
            <UpdatePreferences
                // @ts-expect-error
                emailAddress={props.emailAddress}
                campaignName={props.campaignName}
                allowAudienceUnsubscribe={props.allowAudienceUnsubscribe}
                unsubscribeUrl={url}
                onSuccessfulUnsubscribe={() => {
                    setView(UNSUBSCRIBED_VIEW);
                }}
            />
        );
    }
    if (currentView === UNSUBSCRIBED_VIEW) {
        return (
            <UnsubscribedPage
                resubscribeUrl={url}
                onSuccessfulResubscribe={() => {
                    setView(RESUBSCRIBED_VIEW);
                }}
            />
        );
    }
    if (currentView === RESUBSCRIBED_VIEW) {
        return <ResubscribedPage />;
    }
}
